import { Component, EventEmitter, Output, Input } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { ContactInfoService } from '../services/contact-info.service';

@Component({
  selector: 'prcins-update-contact-info-popup',
  templateUrl: './update-contact-info-popup.component.html',
  styleUrls: ['./update-contact-info-popup.component.css']
})
export class UpdateContactInfoPopupComponent {
  @Output() close = new EventEmitter<void>();
  @Input() userId: string;
  @Input() phoneNumber: string;
  @Input() mobileIndicator: string;
  @Input() doNotShowAgain: string;
  @Input() email: string;

  contactForm: FormGroup;
  errorMessage: string = '';

  constructor(private fb: FormBuilder, private contactInfoService: ContactInfoService) {
    this.contactForm = this.fb.group({
      phoneNumber: ['', [Validators.pattern('^[0-9]{10}$')]],
      mobileIndicator: [false],
      email: ['', [Validators.email, this.emailDomainValidator]],
      doNotShowAgain: [false]
    });
  }

  ngOnInit() {
    // Patch form with @Input values only if they are defined
    const patch: any = {};
    if (this.phoneNumber !== undefined) patch.phoneNumber = this.phoneNumber;
    if (this.mobileIndicator !== undefined) patch.mobileIndicator = this.mobileIndicator === 'Y';
    if (this.email !== undefined) patch.email = this.email;
    if (this.doNotShowAgain !== undefined) patch.doNotShowAgain = this.doNotShowAgain === 'Y';
    this.contactForm.patchValue(patch);
  }

  // Custom validator to check for valid email domain (must have a dot and at least 2+ letters at the end)
  emailDomainValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value) return null;
    // Regex: must have @, then a dot, then at least 2+ letters at the end
    const domainPattern = /@[^@]+\.[a-zA-Z]{2,}$/;
    return domainPattern.test(value) ? null : { invalidDomain: true };
  }

  onSubmit() {
    this.errorMessage = '';
    const doNotShowAgainChecked = this.contactForm.value.doNotShowAgain;
    const phoneEmpty = !this.contactForm.value.phoneNumber;
    const emailEmpty = !this.contactForm.value.email;
    const formValid = this.contactForm.valid;

    // If doNotShowAgain is checked, allow submit even if phone/email are empty/invalid
    if (doNotShowAgainChecked && phoneEmpty && emailEmpty) {
      const payload = {
        doNotShowAgain: 'Y'
      };
      this.contactInfoService.updateContactInfo(this.userId, payload).subscribe(
        () => {
          this.close.emit();
        },
        () => {
          this.errorMessage = 'We were unable to update your contact information. Please try again later.';
        }
      );
      return;
    }

    if (formValid) {
      const payload = {
        phoneNumber: this.contactForm.value.phoneNumber,
        email: this.contactForm.value.email,
        mobileIndicator: this.contactForm.value.mobileIndicator ? 'Y' : 'N',
        doNotShowAgain: this.contactForm.value.doNotShowAgain ? 'Y' : 'N'
      };
      this.contactInfoService.updateContactInfo(this.userId, payload).subscribe(
        () => {
          this.close.emit();
        },
        () => {
          this.errorMessage = 'We were unable to update your contact information. Please try again later.';
        }
      );
    } else {
      this.contactForm.markAllAsTouched();
      // Only show error if email is non-empty and invalid
      if (this.contactForm.value.email && this.contactForm.get('email').invalid) {
        this.errorMessage = 'A valid email address is required.';
      }
    }
  }

  onCancel() {
    this.close.emit();
  }
}
